import { useMutation } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { createColumnHelper, Row } from "@tanstack/react-table";
import {
  formatLocalDate,
  getControlCategoryName,
  getControlName,
  greaterThanToday,
  parseDate,
  service,
  TimeFormats,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  ControlCategoryIcon,
  ControlIcon,
  ExceptionIcon,
  notifyError,
  notifySuccess,
  PlatformIcon,
  Truncate,
} from "@theprelude/ds";
import { useState } from "react";
import { useOverlay } from "@/hooks/use-overlay";
import { Exception } from "@/queries/exceptions";
import { ResponsiveContainer } from "../responsive-container";
import { useTable } from "../table/hooks";
import { None } from "../table/unavaliable";
import { VirtualTable } from "../table/virtual-table";
import { Breadcrumbs } from "./breadcrumbs";
import { DeleteExceptionDialog } from "./delete-exception-modal";

const useDeleteExceptionMutation = () => {
  const authConfig = useAuthConfig();

  return useMutation({
    mutationFn: async (exception: Exception) => {
      if (exception.type === "object") {
        return service(authConfig).scm.deleteObjectException(exception.id);
      }
      return service(authConfig).scm.deletePolicyException({
        instanceId: exception.instance_id,
        policyId: exception.policy_id,
        control: exception.control,
      });
    },
    onSuccess: () => {
      notifySuccess({
        message: "Exception has been deleted.",
        duration: Number.POSITIVE_INFINITY,
        dismissible: true,
      });
    },
    onError: (error) => {
      notifyError({
        message: "Error occurred while trying to delete exception.",
        error,
        dismissible: true,
      });
    },
  });
};

const ExceptionActions = ({ exception }: { exception: Exception }) => {
  const overlay = useOverlay();

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { mutate: deleteException, isPending: isDeleting } =
    useDeleteExceptionMutation();

  const onDelete = () => {
    setShowDeleteConfirmation(false);
    deleteException(exception);
  };

  return (
    <div className="flex items-center gap-1 text-primary-foreground">
      <Button
        intent="pill"
        size="tiny"
        disabled={isDeleting}
        onClick={() =>
          exception.type === "policy"
            ? overlay.open("add-policy-exception", {
                instanceId: exception.instance_id,
                control: exception.control,
                policyId: exception.policy_id,
              })
            : overlay.open("add-object-exception", {
                id: exception.id,
                category: exception.category,
              })
        }
      >
        Edit
      </Button>
      <Button
        intent="pill"
        size="tiny"
        disabled={isDeleting}
        loading={isDeleting}
        onClick={() => setShowDeleteConfirmation(true)}
      >
        Delete
      </Button>
      <DeleteExceptionDialog
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
        onConfirm={onDelete}
      />
    </div>
  );
};

const columnHelper = createColumnHelper<Exception>();
const columns = [
  columnHelper.accessor("alpha", {
    enableHiding: false,
    enableSorting: true,
    sortUndefined: "last",
    cell: (info) => {
      const exception = info.row.original;
      return (
        <Link
          to={window.location.pathname}
          params={(prev) => prev}
          search={(prev) => ({
            ...prev,
            overlay:
              exception.type === "policy"
                ? [
                    "add-policy-exception",
                    {
                      instanceId: exception.instance_id,
                      control: exception.control,
                      policyId: exception.policy_id,
                    },
                  ]
                : [
                    "add-object-exception",
                    { id: exception.id, category: info.row.original.category },
                  ],
          })}
          className="min-w-0"
        >
          {exception.type === "object" ? (
            <Breadcrumbs
              path={[
                {
                  label: getControlCategoryName(exception.category),
                  icon: (
                    <ControlCategoryIcon
                      category={exception.category}
                      className="text-secondary-foreground"
                    />
                  ),
                },
                {
                  label: exception.name ?? "[no name]",
                  icon: <ExceptionIcon />,
                },
              ]}
            />
          ) : (
            <Breadcrumbs
              path={[
                {
                  label: getControlCategoryName(exception.category),
                  icon: <ControlCategoryIcon category={exception.category} />,
                },
                {
                  label: getControlName(exception.control),
                  icon: <ControlIcon control={exception.control} />,
                },
                {
                  label: exception.policy_name,
                  icon: <PlatformIcon platform={exception.policy_platform} />,
                },
              ]}
            />
          )}
        </Link>
      );
    },
    size: 300,
    meta: {
      label: "Exception",
      canExport: false,
    },
  }),
  columnHelper.accessor("expires", {
    enableHiding: false,
    enableSorting: true,
    sortUndefined: "last",
    cell: (info) => {
      const date = info.getValue();

      if (!date) {
        return <None />;
      }
      const formattedExpiration = formatLocalDate(
        parseDate(date),
        TimeFormats.DATE_FIRST,
      );
      return (
        <Truncate text={formattedExpiration}>{formattedExpiration}</Truncate>
      );
    },
    size: 100,
    meta: {
      label: "Expiry",
      canExport: false,
    },
  }),
  columnHelper.display({
    id: "actions",
    enableHiding: false,
    enableResizing: false,
    cell: (info) => <ExceptionActions exception={info.row.original} />,
    size: 100,
    meta: {
      label: " ",
      canExport: false,
    },
  }),
];

export const ExceptionsList = ({
  exceptions,
  loading = false,
}: {
  exceptions: Exception[];
  loading?: boolean;
}) => {
  const table = useTable({
    name: "exceptions",
    data: exceptions,
    columns,
    initialState: {
      sorting: [{ desc: false, id: "alpha" }],
    },
  });

  return (
    <ResponsiveContainer className="flex-1 overflow-hidden">
      {({ height }) => (
        <VirtualTable
          table={table}
          loading={loading}
          height={height}
          emptyState={<EmptyState />}
          rowClassName={getExpiredRowClassName}
        />
      )}
    </ResponsiveContainer>
  );
};

const EmptyState = () => (
  <span className="truncate">No exceptions have been created.</span>
);

const getExpiredRowClassName = (row: Row<Exception>): string => {
  return row.original.expires &&
    greaterThanToday(parseDate(row.original.expires), 0)
    ? "text-secondary-foreground line-through"
    : "";
};
